import { Children, useEffect, useState, type ElementType, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

function escapeSvgText(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** SVG <text> body: plain line or newline-separated tspans stacked on x="50%". */
function svgMaskedTextMarkup(raw: string) {
  const lines = raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
  if (lines.length <= 1) {
    return escapeSvgText(lines[0] ?? raw.trim())
  }
  let body = ''
  lines.forEach((line, i) => {
    const esc = escapeSvgText(line)
    if (i === 0) {
      const up = ((lines.length - 1) * 0.53).toFixed(2)
      body += `<tspan x="50%" dy="-${up}em">${esc}</tspan>`
    } else body += `<tspan x="50%" dy="1.06em">${esc}</tspan>`
  })
  return body
}

export interface VideoTextProps {
  src: string
  className?: string
  autoPlay?: boolean
  muted?: boolean
  loop?: boolean
  preload?: 'auto' | 'metadata' | 'none'
  children: ReactNode
  fontSize?: string | number
  fontWeight?: string | number
  textAnchor?: string
  dominantBaseline?: string
  fontFamily?: string
  as?: ElementType
}

export function VideoText({
  src,
  children,
  className = '',
  autoPlay = true,
  muted = true,
  loop = true,
  preload = 'auto',
  fontSize = 20,
  fontWeight = 'bold',
  textAnchor = 'middle',
  dominantBaseline = 'middle',
  fontFamily = 'sans-serif',
  as: Component = 'div',
}: VideoTextProps) {
  const [svgMask, setSvgMask] = useState('')
  const content = Children.toArray(children)
    .map(child => {
      if (typeof child === 'string' || typeof child === 'number') return String(child)
      return ''
    })
    .join('')

  const srLabel = content.includes('\n') ? content.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim() : content

  useEffect(() => {
    const updateSvgMask = () => {
      const responsiveFontSize = typeof fontSize === 'number' ? `${fontSize}vw` : fontSize
      const inner = svgMaskedTextMarkup(content)
      const newSvgMask = `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"><text x="50%" y="50%" font-size="${escapeSvgText(responsiveFontSize)}" font-weight="${escapeSvgText(String(fontWeight))}" text-anchor="${escapeSvgText(textAnchor)}" dominant-baseline="${escapeSvgText(dominantBaseline)}" font-family="${escapeSvgText(fontFamily)}">${inner}</text></svg>`
      setSvgMask(newSvgMask)
    }

    updateSvgMask()
    window.addEventListener('resize', updateSvgMask)
    return () => window.removeEventListener('resize', updateSvgMask)
  }, [content, fontSize, fontWeight, textAnchor, dominantBaseline, fontFamily])

  const dataUrlMask = `url("data:image/svg+xml,${encodeURIComponent(svgMask)}")`

  return (
    <Component className={cn('relative isolate h-full min-h-[12rem] w-full', className)}>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          maskImage: dataUrlMask,
          WebkitMaskImage: dataUrlMask,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        }}
      >
        <video className="h-full w-full object-cover" autoPlay={autoPlay} muted={muted} loop={loop} preload={preload} playsInline>
          <source src={src} />
        </video>
      </div>

      <span className="sr-only">{srLabel}</span>
    </Component>
  )
}
